/**
 * O recorte da tela de menções: o tipo, os valores vazios e a tradução
 * pro formato que vai salvo no servidor.
 *
 * Extraído do `MentionsPage` quando os filtros salvos chegaram — os
 * diálogos de salvar/carregar precisam do MESMO vocabulário que a tela,
 * e uma segunda cópia divergiria no primeiro campo novo.
 *
 * **A ida e a volta são cópia de string, de propósito.** A tentação é
 * guardar o filtro no formato que a consulta usa (`date_from` como
 * instante em UTC), mas aí `31/08 23:59` em Brasília vira
 * `2026-09-01T02:59Z`, e desmontar isso na volta devolve o dia errado e
 * uma hora que ninguém digitou. Guardando data e hora separadas, como a
 * pessoa preencheu, recarregar um filtro reproduz exatamente os mesmos
 * chips. A conversão pra UTC continua num lugar só: a montagem da
 * consulta, no `MentionsPage`.
 *
 * **Cada dimensão é um conjunto + uma flag de exclusão** desde
 * 2026-08-27, quando a tela passou pro modelo de multi-seleção que o
 * dashboard já usava. É o que permite "as favoráveis e as positivas" ou
 * "tudo menos citação" — perguntas que um campo de valor único não
 * sabia fazer.
 *
 * Na TELA, as dimensões de catálogo (marca, tema, grupo-tema,
 * porta-voz, grupo de publicador) são selecionadas por NOME: elas são
 * cadastradas por marca, então "Sustentabilidade" pode ser três linhas
 * com ids diferentes numa empresa de três marcas, e o nome é a
 * identidade que a pessoa reconhece (decisão da dona, 2026-08-27). A
 * tradução nome → ids acontece na borda da consulta.
 *
 * No filtro SALVO (v3, 2026-09-25, decisão da dona: "nada por nome"),
 * cada opção escolhida vai como `{label, ids}` — os ids que carregavam
 * aquele nome na hora de salvar, mais o nome como foto. Renomear o tema
 * deixa de apagar o filtro; o chip mostra o nome de HOJE (resolvido
 * pelo id) e cai no rótulo gravado quando o id saiu do catálogo. O
 * preço, aceito: marca cadastrada depois com tema de mesmo nome não
 * entra num filtro salvo antes — a pessoa regrava.
 */

import { foldText } from "@/shared/utils/text-search";
import {
  MENTION_SEARCH_FIELDS,
  type MentionSearchField
} from "@/shared/vocabulary/mention-search-fields";

/** O bucket "sem valor" de uma dimensão. É o MESMO sentinela do
 *  servidor (`core/database/facets.py::NULL_FACET_KEY`) e do dashboard
 *  — em polaridade ele quer dizer "sem classificação", que era uma
 *  flag própria até 2026-08-27. */
export const NO_VALUE = "__none__";

/** O nome antigo do sentinela de polaridade, ainda gravado nos filtros
 *  salvos v1 e em deep links. Lido na entrada, nunca escrito. */
export const LEGACY_UNCLASSIFIED = "__unclassified__";

export const APPROACH_CHOICES = [
  { value: "protagonist", label: "Protagonista" },
  { value: "supporting", label: "Coadjuvante" },
  { value: "citation", label: "Citação" },
];

export const SOURCE_CHOICES = [
  { value: "press", label: "Imprensa" },
  { value: "network", label: "Redes sociais" },
];

/** O vocabulário de tier e formato. Mora AQUI, junto das outras listas
 *  fechadas, e não na tela que o desenha: três consumidores o usam (o
 *  painel de menções, os chips do recorte e a sanitização da
 *  querystring), e a segunda cópia é sempre a que envelhece.
 *
 *  Vem do cadastro de `publisher_tier`/`vehicle_tier` no servidor; a
 *  aba Ajustes tem a lista escrita à mão, e unificar as duas é conserto
 *  de outra entrega. */
export const TIER_CHOICES = [
  { value: "supertier", label: "Supertier" },
  { value: "tier1", label: "Tier 1" },
  { value: "tier2", label: "Tier 2" },
  { value: "tier3", label: "Tier 3" },
  { value: "tier4", label: "Tier 4" },
  { value: "tier5", label: "Tier 5" },
];

export const FORMAT_CHOICES = [
  { value: "P", label: "P" },
  { value: "M", label: "M" },
  { value: "G", label: "G" },
];

/** Tipo de mídia (2026-11-01): a chave de `core/vocabulary.py::MEDIA_TYPES`
 *  e o rótulo de `core/media_type.py`. Só imprensa; o "Não informado"
 *  (`NO_VALUE`) é a imprensa sem tipo conhecido e entra na caixa à parte. */
export const MEDIA_TYPE_CHOICES = [
  { value: "print", label: "Impresso" },
  { value: "online", label: "Online" },
  { value: "tv", label: "TV" },
  { value: "radio", label: "Rádio" },
];

/** As dimensões multi-seleção da tela. A ordem aqui é a ordem em que
 *  elas aparecem no painel e nos chips. */
export const DIMENSIONS = [
  "polarities",
  "approaches",
  "sources",
  "channels",
  // Veículo (2026-10-19): o par de Canal pro lado da imprensa. Os dois
  // viajam por ID (Canal desde 2026-11-03; era o nome cru do post).
  "vehicles",
  "brands",
  "themes",
  "themeGroups",
  "spokespersons",
  // Mensagem-chave (2026-11-01): catálogo por marca, como porta-voz, com
  // a opção "Sem mensagem-chave" (`NO_VALUE`), que VIAJA pra amostra como
  // `no_key_message`.
  "keyMessages",
  "publisherGroups",
  "tiers",
  "formats",
  // Tipo de mídia (2026-11-01): vocabulário fechado, só imprensa. O
  // "Não informado" (`NO_VALUE`) também viaja.
  "mediaTypes",
  "influencerTypes",
  // Clipadora (2026-10-31): a clipadora do UPLOAD do post. Faceta de
  // valores e INCLUSÃO PURA — o modo excluir nunca é ligado (o painel e
  // a barra o escondem) e o servidor não o aceita. É dimensão que
  // VIAJA pro relatório (`clipping_services`).
  "clippingServices",
  // "Editado por" (2026-09-21): o valor é o `sub` de quem corrigiu.
  // Dimensão da TELA — fica fora de `RECORTE_DIMENSIONS`, porque quem
  // corrigiu é atributo da trilha, não da menção, e o relatório não a
  // congela.
  "editors",
] as const;

/**
 * As dimensões cujo VALOR selecionado é um nome que precisa virar id
 * antes de ir pro servidor (`idsFor`/`idsByName` de `filter-options`).
 *
 * Elas dependem do catálogo estar carregado — e o catálogo só carrega
 * quando o painel "Mais filtros" abre. Um filtro SALVO que traga uma
 * delas chega antes disso: sem o catálogo, `facetIds` devolve `[]` e a
 * consulta sai SEM o recorte, com o chip na tela dizendo que ele está
 * aplicado. É o bug que a varredura de 2026-08-29 encontrou.
 *
 * `brands` fica de FORA de propósito: o catálogo de marcas carrega no
 * mount da tela, não no painel, então ele já está lá.
 */
export const CATALOG_DIMENSIONS = [
  "themes",
  "themeGroups",
  "spokespersons",
  "keyMessages",
  "publisherGroups",
  // Porte do publicador (2026-11-03): por id, lista inteira e pequena (as
  // faixas da empresa). Antes era o NOME da faixa, e "USUÁRIO COMUM" não
  // casava com o "Usuário Comum" gravado.
  "influencerTypes",
] as const;

/** As dimensões que o filtro SALVO guarda por id (`{label, ids}`): as
 *  do painel, a marca, o veículo e o canal. `editors` fica de fora — o
 *  `sub` já é id.
 *
 *  `vehicles` e `channels` estão AQUI e NÃO em `CATALOG_DIMENSIONS`:
 *  aquela lista gateia a consulta em "o catálogo inteiro chegou", e eles
 *  não têm catálogo inteiro (veículo são ~14 mil, canal cresce com a
 *  importação) — chegam por BUSCA, e o que a tela não conhece o filtro
 *  salvo resolve pelos ids gravados. */
export const ID_DIMENSIONS = ["brands", "vehicles", "channels", ...CATALOG_DIMENSIONS] as const;

export type Dimension = (typeof DIMENSIONS)[number];
export type IdDimension = (typeof ID_DIMENSIONS)[number];

/** O nome de cada dimensão na tela. Mora aqui (e não na página) porque
 *  três consumidores o leem: o painel, os chips do recorte e o
 *  formulário do relatório. */
export const DIMENSION_LABELS: Record<Dimension, string> = {
  polarities: "Polaridade",
  approaches: "Abordagem",
  sources: "Fonte",
  channels: "Canal",
  vehicles: "Veículo",
  brands: "Marca",
  themes: "Subtema",
  themeGroups: "Tema",
  spokespersons: "Porta-voz",
  keyMessages: "Mensagem-chave",
  publisherGroups: "Grupo de publicador",
  tiers: "Tier do veículo",
  formats: "Formato",
  mediaTypes: "Tipo de mídia",
  influencerTypes: "Porte do publicador",
  clippingServices: "Clipadora",
  editors: "Editado por",
};

export function isIdDimension(dimension: Dimension): dimension is IdDimension {
  return (ID_DIMENSIONS as readonly string[]).includes(dimension);
}

/** Uma opção gravada de dimensão de catálogo: o nome de quando foi
 *  salva e os ids que ele valia. */
export type SavedItem = { label: string; ids: number[] };

/** Os ids gravados por nome, por dimensão — o que a tela consulta
 *  quando um nome do filtro salvo não existe mais no catálogo vivo. */
export type SavedFacetIds = Partial<Record<IdDimension, Map<string, number[]>>>;

/** O mínimo do catálogo que a volta do filtro salvo precisa. */
export type IdResolver = {
  idsByName: Map<string, number[]>;
  nameById: Map<number, string>;
};

export type Facet = { values: Set<string>; exclude: boolean };

/** O recorte de polaridade é SÓ "Sem classificação" — o que não gera
 *  relatório. Em Excluir é o contrário (só o que já foi classificado),
 *  e travava os botões de gerar (relato de 2026-10-27). */
export function onlyUnclassified(polarities: Facet): boolean {
  return (
    !polarities.exclude &&
    polarities.values.size === 1 &&
    polarities.values.has(NO_VALUE)
  );
}

export type MentionFilters = {
  facets: Record<Dimension, Facet>;
  dateFrom: string;
  timeFrom: string;
  dateTo: string;
  timeTo: string;
  minCena: string;
  /** Valoração mínima, em reais inteiros. String como os irmãos acima
   *  porque o estado é o que está DIGITADO no input — o saneamento pra
   *  número acontece no envio (`sanitizeMinAdValue`). */
  minAdValue: string;
  /** "Só descartadas" (2026-09-21): a lista vira SÓ o que foi
   *  descartado — o caminho de recuperação além do "desfazer". Fica
   *  FORA do filtro salvo e do recorte do relatório de propósito: é
   *  modo de visualização, não recorte de análise. */
  onlyDiscarded: boolean;
  /** Revisão (2026-10-03): as duas, só as revisadas ou só as que
   *  faltam. Ao contrário de "só descartadas", ENTRA no filtro salvo e
   *  VIAJA pro relatório — é recorte de análise. */
  reviewed: "all" | "yes" | "no";
  /** Citação no título (2026-10-22): só imprensa (meio-escopo — a rede
   *  passa inteira). "yes" = só as que citam, "no" = só as que não
   *  (TV e rádio não pontuam citação e contam como "sem"). VIAJA pro
   *  relatório, como a revisão. */
  titleCitation: "all" | "yes" | "no";
  /** Subtema em destaque (2026-11-01): "yes" = só as menções que TÊM um
   *  (escolhido à mão, ou a sugestão da regra); "no" = só as que não têm.
   *  VIAJA pra amostra. */
  themeHighlight: "all" | "yes" | "no";
  /** Post em mais de um cliente (2026-11-01): "yes" = o mesmo post
   *  também é menção de outra marca do escopo (o "também em" da linha);
   *  "no" = só desta. VIAJA pra amostra. */
  sharedPost: "all" | "yes" | "no";
  /** Edição (2026-10-07): as duas, só as que alguém mexeu ou só as
   *  intocadas. É o "Editado por" sem o quem. Entra no filtro salvo;
   *  NÃO viaja pro relatório (é atributo da trilha, como o editor). */
  edited: "all" | "yes" | "no";
};

/**
 * Valoração mínima saneada: reais inteiros >= 1, ou `null`.
 *
 * `Math.trunc` e não `parseInt`: "1500.5" digitado (ou colado na URL)
 * vira 1500 em vez de virar 422 depois do clique. O piso é 1 porque
 * "≥ R$ 0" não recorta nada — o servidor recusa com `ge=1`, e sanear
 * aqui é o que evita a viagem só pra levar um erro de volta.
 *
 * Mora aqui, e não em `report-recorte.ts`, porque aquele arquivo já
 * importa deste — a volta seria ciclo. As duas telas usam a MESMA
 * função de propósito: se a de menções aceitasse um valor que a de
 * relatório recusa, o botão "gerar" levaria um recorte diferente do
 * que a lista mostrou.
 */
export function sanitizeMinAdValue(
  raw: string | number | null | undefined
): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const numero = Math.trunc(Number(raw));
  if (!Number.isFinite(numero) || numero < 1) return null;
  return numero;
}

const emptyFacet = (): Facet => ({ values: new Set<string>(), exclude: false });

/**
 * Limpar (ou desmarcar) um grupo de tema derruba os temas DELE do
 * filtro de tema — pedido da galera de negócio (2026-09-21): "quando
 * limpar o filtro de tema, apagar o subtema também".
 *
 * A regra, por tema selecionado: sai se pertence a algum dos grupos
 * removidos E não pertence a nenhum grupo que continua selecionado.
 * Tema fora do mapa (sem grupo, ou catálogo ainda não carregado) fica
 * — derrubar o que não se conhece seria apagar um recorte que a
 * pessoa pediu.
 *
 * Devolve um estado NOVO (a facet de tema é substituída, o resto é
 * o mesmo objeto), e não mexe na facet de grupo: quem chama já a
 * alterou, e esta função só olha `removedGroups` × `filters`.
 */
export function dropThemesOfGroups(
  filters: MentionFilters,
  removedGroups: Iterable<string>,
  groupsByTheme: Map<string, Set<string>>
): MentionFilters {
  const removed = new Set(removedGroups);
  if (!removed.size) return filters;
  const remaining = filters.facets.themeGroups.values;
  const themes = new Set<string>();
  for (const theme of filters.facets.themes.values) {
    const groups = groupsByTheme.get(theme);
    if (!groups) {
      themes.add(theme);
      continue;
    }
    const touched = [...groups].some(group => removed.has(group));
    const kept = [...groups].some(group => remaining.has(group));
    if (!touched || kept) themes.add(theme);
  }
  if (themes.size === filters.facets.themes.values.size) return filters;
  return {
    ...filters,
    facets: {
      ...filters.facets,
      themes: { ...filters.facets.themes, values: themes },
    },
  };
}

export const emptyFilters = (): MentionFilters => ({
  facets: Object.fromEntries(
    DIMENSIONS.map(dimension => [dimension, emptyFacet()])
  ) as Record<Dimension, Facet>,
  dateFrom: "",
  timeFrom: "",
  dateTo: "",
  timeTo: "",
  minCena: "",
  minAdValue: "",
  onlyDiscarded: false,
  reviewed: "all",
  titleCitation: "all",
  themeHighlight: "all",
  sharedPost: "all",
  edited: "all",
});

/** O que vai salvo. Só o que a tela sabe RESTAURAR: um campo que o
 *  servidor filtra mas a tela não desenha viraria um recorte invisível,
 *  que ninguém consegue ver nem remover. */
/** Uma dimensão gravada: `values` (v2, e ainda hoje nas dimensões que
 *  não são de catálogo) ou `items` (v3, nas de catálogo). */
export type FacetCriteria = {
  values?: string[];
  items?: SavedItem[];
  exclude?: boolean;
};

export type MentionFilterCriteria = {
  schema_version: number;
  q?: string;
  /** Onde `q` procura. Qualificador do `q`: só é gravado com busca e fora
   *  do padrão `all` (ausente = tudo), e NÃO conta como recorte. */
  q_in?: MentionSearchField;
  /** Uma chave por dimensão, com a seleção + exclusão. */
  facets?: Partial<Record<Dimension, FacetCriteria>>;
  date_from?: string;
  time_from?: string;
  date_to?: string;
  time_to?: string;
  min_icena?: number;
  /** Inteiro >= 1 — o servidor recusa 0 e fracionário (a mesma trava
   *  que o recorte de geração, porque este filtro viaja pra lá). */
  min_ad_value?: number;
  /** `true` = só revisadas, `false` = só as que faltam; ausente = as
   *  duas. */
  reviewed?: boolean;
  /** `true` = imprensa que cita a marca no título, `false` = que não
   *  cita; ausente = sem filtro. */
  title_citation?: boolean;
  /** `true` = com subtema em destaque, `false` = sem; ausente = as duas. */
  theme_highlight?: boolean;
  /** `true` = post em mais de um cliente, `false` = só neste; ausente =
   *  os dois. */
  shared_post?: boolean;
  /** `true` = só as editadas, `false` = só as intocadas; ausente = as
   *  duas. */
  edited?: boolean;
  // --- v1, só LEITURA (os salvos antes de 2026-08-27) ---------------
  polarity?: string;
  only_unclassified?: boolean;
  approach?: string;
  brand_id?: number;
};

export const CRITERIA_SCHEMA_VERSION = 3;

export type MentionFilterState = {
  query: string;
  /** Onde a busca procura; ausente = `all`. */
  queryIn?: MentionSearchField;
  filters: MentionFilters;
  /** Só na VOLTA de um filtro salvo: os ids gravados por nome, pra
   *  consulta usar quando o nome não resolve no catálogo vivo. */
  savedIds?: SavedFacetIds;
};

/**
 * `resolveIds` é quem sabe nome → ids da tela (catálogo vivo mais os
 * ids de um filtro salvo aplicado): a criteria grava o que a consulta
 * mandaria naquele instante. Vem por parâmetro porque este módulo não
 * tem catálogo, e um resolvedor que devolvesse `[]` gravaria uma foto
 * vazia em silêncio.
 */
export function criteriaFromState(
  state: MentionFilterState,
  resolveIds: (dimension: IdDimension, names: Set<string>) => number[]
): MentionFilterCriteria {
  const { query, filters } = state;
  const criteria: MentionFilterCriteria = { schema_version: CRITERIA_SCHEMA_VERSION };

  const trimmed = query.trim();
  if (trimmed) {
    criteria.q = trimmed;
    if (state.queryIn && state.queryIn !== "all") criteria.q_in = state.queryIn;
  }

  const facets: NonNullable<MentionFilterCriteria["facets"]> = {};
  for (const dimension of DIMENSIONS) {
    const facet = filters.facets[dimension];
    if (!facet.values.size) continue;
    // Chave ausente quando falsa: exclusão de nada é "sem filtro", e
    // duas grafias da mesma intenção fariam o diff de auditoria
    // acusar mudança onde não houve.
    const exclude = facet.exclude ? { exclude: true } : {};
    if (isIdDimension(dimension)) {
      facets[dimension] = {
        items: [...facet.values].map(label => ({
          label,
          ids: resolveIds(dimension, new Set([label])),
        })),
        ...exclude,
      };
    } else {
      facets[dimension] = { values: [...facet.values], ...exclude };
    }
  }
  if (Object.keys(facets).length) criteria.facets = facets;

  if (filters.dateFrom) criteria.date_from = filters.dateFrom;
  if (filters.timeFrom) criteria.time_from = filters.timeFrom;
  if (filters.dateTo) criteria.date_to = filters.dateTo;
  if (filters.timeTo) criteria.time_to = filters.timeTo;
  if (filters.minCena) criteria.min_icena = Number(filters.minCena);
  // Saneado antes de salvar: guardar "1500.5" faria o servidor recusar
  // o filtro salvo na hora de aplicá-lo, e a pessoa não teria como
  // adivinhar por quê. `null` (vazio ou abaixo do piso) não vira chave.
  const minAdValue = sanitizeMinAdValue(filters.minAdValue);
  if (minAdValue !== null) criteria.min_ad_value = minAdValue;
  if (filters.reviewed !== "all") criteria.reviewed = filters.reviewed === "yes";
  if (filters.titleCitation !== "all") {
    criteria.title_citation = filters.titleCitation === "yes";
  }
  if (filters.themeHighlight !== "all") {
    criteria.theme_highlight = filters.themeHighlight === "yes";
  }
  if (filters.sharedPost !== "all") criteria.shared_post = filters.sharedPost === "yes";
  if (filters.edited !== "all") criteria.edited = filters.edited === "yes";

  return criteria;
}

/**
 * O caminho de volta, tolerante às DUAS versões.
 *
 * Um filtro salvo v1 tem `polarity`/`approach`/`brand_id` escalares e
 * `only_unclassified` como flag; ele continua no banco pra sempre, e
 * carregá-lo tem que reproduzir o mesmo recorte — valor único vira
 * conjunto de um. Regravar (o diálogo de salvar por cima) o promove a
 * v2, que é o comportamento desejado.
 *
 * `showsCena` entra por parâmetro (é hook, não dá pra chamar aqui): com
 * o CENA escondido, a nota mínima do filtro salvo é ignorada em vez de
 * virar estado invisível na tela.
 *
 * A marca é a única dimensão de catálogo que ainda viaja por ID no
 * formato v1 — na v2 ela guarda o nome, como as outras. Um filtro v1
 * com `brand_id` é lido pelo id e traduzido pelo chamador, que tem a
 * lista de marcas em mãos.
 *
 * Na v3 as dimensões de catálogo vêm como `{label, ids}`: o chip ganha
 * o nome de HOJE quando todos os ids do item ainda respondem por um
 * mesmo nome no catálogo (`catalogs`), e o rótulo gravado quando não —
 * catálogo ainda não carregado, id desativado, ou renomeado por marca.
 * Os ids gravados voltam em `savedIds`, e são o que a consulta usa pra
 * um nome que o catálogo vivo não resolve.
 */
export function stateFromCriteria(
  criteria: MentionFilterCriteria,
  options: {
    showsCena: boolean;
    brandNameById?: (id: number) => string | undefined;
    catalogs?: Partial<Record<IdDimension, IdResolver>>;
  }
): MentionFilterState {
  const filters = emptyFilters();
  const savedIds: SavedFacetIds = {};

  if (criteria.facets) {
    for (const dimension of DIMENSIONS) {
      const salvo = criteria.facets[dimension];
      if (!salvo) continue;
      const values = new Set<string>(salvo.values ?? []);
      if (salvo.items && isIdDimension(dimension)) {
        const porNome = new Map<string, number[]>();
        for (const item of salvo.items) {
          const nome = currentName(item, options.catalogs?.[dimension]) ?? item.label;
          values.add(nome);
          porNome.set(nome, [...(porNome.get(nome) ?? []), ...item.ids]);
        }
        savedIds[dimension] = porNome;
      }
      filters.facets[dimension] = {
        values,
        exclude: Boolean(salvo.exclude),
      };
    }
  }

  // --- v1: escalares viram conjunto de um -----------------------------
  if (criteria.only_unclassified) filters.facets.polarities.values.add(NO_VALUE);
  if (criteria.polarity) {
    filters.facets.polarities.values.add(
      criteria.polarity === LEGACY_UNCLASSIFIED ? NO_VALUE : criteria.polarity
    );
  }
  if (criteria.approach) filters.facets.approaches.values.add(criteria.approach);
  if (criteria.brand_id != null) {
    const nome = options.brandNameById?.(criteria.brand_id);
    if (nome) filters.facets.brands.values.add(nome);
  }

  filters.dateFrom = criteria.date_from ?? "";
  filters.timeFrom = criteria.time_from ?? "";
  filters.dateTo = criteria.date_to ?? "";
  filters.timeTo = criteria.time_to ?? "";
  if (options.showsCena && criteria.min_icena != null) {
    filters.minCena = String(criteria.min_icena);
  }
  // Sem gate de `showsCena`, ao contrário da nota mínima: valoração não
  // é CENA — é fato da planilha, e uma empresa que esconde o CENA
  // continua querendo filtrar por valor publicitário.
  if (criteria.min_ad_value != null) {
    filters.minAdValue = String(criteria.min_ad_value);
  }
  if (typeof criteria.reviewed === "boolean") {
    filters.reviewed = criteria.reviewed ? "yes" : "no";
  }
  if (typeof criteria.title_citation === "boolean") {
    filters.titleCitation = criteria.title_citation ? "yes" : "no";
  }
  if (typeof criteria.theme_highlight === "boolean") {
    filters.themeHighlight = criteria.theme_highlight ? "yes" : "no";
  }
  if (typeof criteria.shared_post === "boolean") {
    filters.sharedPost = criteria.shared_post ? "yes" : "no";
  }
  if (typeof criteria.edited === "boolean") {
    filters.edited = criteria.edited ? "yes" : "no";
  }

  // Vocabulário fechado: valor desconhecido cai em `all`, e sem busca o
  // campo não significa nada.
  const queryIn: MentionSearchField =
    criteria.q && criteria.q_in && MENTION_SEARCH_FIELDS.includes(criteria.q_in)
      ? criteria.q_in
      : "all";
  return { query: criteria.q ?? "", queryIn, filters, savedIds };
}

/** O nome atual de um item gravado: só quando TODOS os ids dele ainda
 *  respondem por um mesmo nome — dois nomes diferentes é uma opção da
 *  tela que virou duas, e aí vale o rótulo gravado. */
function currentName(item: SavedItem, catalog: IdResolver | undefined): string | undefined {
  if (!catalog || !item.ids.length) return undefined;
  const nomes = new Set(item.ids.map(id => catalog.nameById.get(id)));
  if (nomes.size !== 1) return undefined;
  const [nome] = nomes;
  return nome;
}

/** Último degrau da resolução de nome: o catálogo vivo comparado SEM caixa
 *  e SEM acento. É o que faz o nome de um filtro salvo ANTIGO ("Twitter",
 *  "Usuário Comum", gravados quando a comparação era por texto) achar o
 *  cadastro de hoje ("TWITTER", "USUÁRIO COMUM"). */
function foldedMatch(
  name: string,
  catalog: { idsByName: Map<string, number[]> } | null
): number[] {
  if (!catalog) return [];
  const alvo = foldText(name);
  const ids: number[] = [];
  for (const [nome, achados] of catalog.idsByName) {
    if (foldText(nome) === alvo) ids.push(...achados);
  }
  return ids;
}

/**
 * Os ids que a consulta manda pra um conjunto de nomes: o catálogo vivo
 * primeiro (é a identidade da tela), e o que o filtro salvo trouxe pra
 * nome que o catálogo não conhece. Sem o segundo degrau, um tema
 * renomeado ou desativado sumiria do recorte em silêncio — que é o
 * que a v3 existe pra evitar.
 */
export function resolveFacetIds(
  names: Iterable<string>,
  catalog: { idsByName: Map<string, number[]> } | null,
  saved: Map<string, number[]> | undefined
): number[] {
  const ids: number[] = [];
  for (const name of names) {
    const vivos = catalog?.idsByName.get(name);
    if (vivos?.length) ids.push(...vivos);
    else if (saved?.get(name)?.length) ids.push(...saved.get(name)!);
    else ids.push(...foldedMatch(name, catalog));
  }
  return [...new Set(ids)];
}

/** Um id que não casa nada: os ids do catálogo são seriais a partir de 1. */
export const NO_MATCH_ID = 0;

/** Os ids que uma dimensão manda ao servidor, ou `undefined` (sem
 *  filtro). **Inclusão sem nenhum id resolvido manda `NO_MATCH_ID`**
 *  (2026-10-05): um nome escolhido que não vira id (tema renomeado ou
 *  apagado desde o filtro salvo, marca de outro escopo) sumia da
 *  consulta, e a lista mostrava TUDO com o chip dizendo que o filtro
 *  estava aplicado. Incluir "ninguém" é recorte vazio; excluir
 *  "ninguém" não exclui nada, e aí não há o que mandar. */
export function idsToSend(ids: number[], exclude: boolean): number[] | undefined {
  if (ids.length) return ids;
  return exclude ? undefined : [NO_MATCH_ID];
}

/** Quantos recortes o filtro carrega — o mesmo número que a tela mostra
 *  no botão "Mais filtros", pra o preview do diálogo não contar
 *  diferente do que a pessoa vê.
 *
 *  Conta DIMENSÕES, não chaves: uma dimensão com exclusão continua
 *  sendo um recorte só, e contar o par `values`+`exclude` como dois
 *  faria o número divergir do contador da tela. */
export function criteriaSize(criteria: MentionFilterCriteria): number {
  const escalares = [
    "q", "date_from", "time_from", "date_to", "time_to", "min_icena",
    "min_ad_value", "reviewed", "title_citation", "theme_highlight",
    "shared_post", "edited"
  ];
  const v1 = ["polarity", "only_unclassified", "approach", "brand_id"];
  return (
    escalares.filter(key => criteria[key as keyof MentionFilterCriteria] != null).length +
    v1.filter(key => criteria[key as keyof MentionFilterCriteria] != null).length +
    Object.keys(criteria.facets ?? {}).length
  );
}
